import { useEffect, useRef, useState } from 'react';
import { X, Upload, Loader2, Film, Music, Image, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { mediaApi } from '../../api/client';
import { resolveMediaUrl } from '../../lib/mediaUrl';

const ALL_TYPE_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'image', label: 'Images' },
  { key: 'video', label: 'Videos' },
  { key: 'audio', label: 'Audio' },
];

function buildVisibleFilters(allowedTypes) {
  if (!allowedTypes) return ALL_TYPE_FILTERS;
  const typeFilters = ALL_TYPE_FILTERS.filter(f => f.key !== 'all' && allowedTypes.includes(f.key));
  if (allowedTypes.length > 1) return [{ key: 'all', label: 'All' }, ...typeFilters];
  return typeFilters;
}

function getInitialFilter(allowedTypes, defaultFilter) {
  if (allowedTypes?.length === 1) return allowedTypes[0];
  return defaultFilter;
}

// allowedTypes: e.g. ['image','video'] — restricts visible tabs and assets
export default function MediaLibraryModal({ isOpen, onClose, onSelect, defaultFilter = 'all', allowedTypes = null }) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState(() => getInitialFilter(allowedTypes, defaultFilter));
  const [search, setSearch] = useState('');
  const fileInputRef = useRef(null);

  const visibleFilters = buildVisibleFilters(allowedTypes);

  // Load on open (reset to initial filter)
  useEffect(() => {
    if (!isOpen) return;
    const initial = getInitialFilter(allowedTypes, defaultFilter);
    setFilter(initial);
    setSearch('');
    loadMedia(initial);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch when tab changes
  useEffect(() => {
    if (!isOpen) return;
    loadMedia(filter);
  }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadMedia(activeFilter) {
    setLoading(true);
    try {
      const params = activeFilter && activeFilter !== 'all' ? { type: activeFilter } : {};
      const result = await mediaApi.list(params);
      setAssets(result?.items ?? result ?? []);
    } catch {
      toast.error('Failed to load media');
    } finally {
      setLoading(false);
    }
  }

  async function handleUpload(files) {
    if (!files?.length) return;
    setUploading(true);
    try {
      const added = [];
      for (const file of Array.from(files)) {
        const asset = await mediaApi.upload(file);
        added.push(asset);
      }
      // Only prepend if the uploaded type matches the current tab
      setAssets(prev => [
        ...added.filter(a => filter === 'all' || a.type === filter),
        ...prev,
      ]);
      toast.success(`${added.length} file${added.length > 1 ? 's' : ''} uploaded`);
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  }

  const filtered = assets.filter(a => {
    const matchAllowed = !allowedTypes || allowedTypes.includes(a.type);
    const matchSearch = !search || (a.originalFilename || '').toLowerCase().includes(search.toLowerCase());
    return matchAllowed && matchSearch;
  });

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="relative w-full max-w-3xl flex flex-col rounded-2xl overflow-hidden"
        style={{
          background: 'var(--t-bg)',
          border: '1px solid var(--t-border)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
          maxHeight: '82vh',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b flex-shrink-0" style={{ borderColor: 'var(--t-border)' }}>
          <h2 className="font-semibold text-sm" style={{ color: 'var(--t-text)' }}>Media Library</h2>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-lg flex items-center justify-center transition-opacity hover:opacity-60"
            style={{ background: 'var(--t-panel)' }}
          >
            <X size={14} style={{ color: 'var(--t-muted)' }} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex flex-shrink-0 border-b" style={{ borderColor: 'var(--t-border)' }}>
          {visibleFilters.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className="px-4 py-2.5 text-xs font-medium transition-colors"
              style={{
                color: filter === key ? 'var(--brand-primary)' : 'var(--t-muted)',
                background: 'transparent',
                border: 'none',
                borderBottom: filter === key ? '2px solid var(--brand-primary)' : '2px solid transparent',
                marginBottom: -1,
                cursor: 'pointer',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-2.5 px-5 py-2.5 border-b flex-shrink-0" style={{ borderColor: 'var(--t-border)' }}>
          {/* Search */}
          <div className="relative flex-1">
            <Search size={11} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--t-muted)' }} />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search files…"
              className="w-full pl-7 pr-3 py-1.5 rounded-lg text-xs"
              style={{ background: 'var(--t-panel)', border: '1px solid var(--t-border)', color: 'var(--t-text)', outline: 'none' }}
            />
          </div>

          {/* Upload */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={allowedTypes ? allowedTypes.map(t => `${t}/*`).join(',') : 'image/*,video/*,audio/*'}
            className="hidden"
            onChange={e => { if (e.target.files?.length) handleUpload(e.target.files); e.target.value = ''; }}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all disabled:opacity-50 flex-shrink-0"
            style={{ background: 'var(--brand-primary)', color: '#fff' }}
          >
            {uploading ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
            Upload
          </button>
        </div>

        {/* Count */}
        {!loading && assets.length > 0 && (
          <div className="px-5 py-2 border-b flex-shrink-0" style={{ borderColor: 'var(--t-border)' }}>
            <span className="text-[11px]" style={{ color: 'var(--t-muted)' }}>
              {filtered.length} {filtered.length === 1 ? 'file' : 'files'}
              {filter !== 'all' ? ` · ${filter}` : ''}
            </span>
          </div>
        )}

        {/* Grid */}
        <div
          className="flex-1 overflow-y-auto p-4"
          onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); handleUpload(e.dataTransfer.files); }}
        >
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 size={24} className="animate-spin" style={{ color: 'var(--brand-primary)' }} />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ background: 'var(--t-panel)' }}>
                <Image size={22} style={{ color: 'var(--t-muted)' }} />
              </div>
              <p className="text-sm font-medium" style={{ color: 'var(--t-text)' }}>
                {assets.length === 0 ? 'No media files yet' : 'No files match your filter'}
              </p>
              <p className="text-xs" style={{ color: 'var(--t-muted)' }}>
                {assets.length === 0 ? 'Upload files using the button above' : 'Try a different filter or search term'}
              </p>
            </div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                gap: '10px',
              }}
            >
              {filtered.map(asset => (
                <AssetCard
                  key={asset.id}
                  asset={asset}
                  onSelect={() => { onSelect(asset.url); onClose(); }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AssetCard({ asset, onSelect }) {
  const TYPE_COLORS = {
    image: 'rgba(99,102,241,0.85)',
    video: 'rgba(236,72,153,0.85)',
    audio: 'rgba(16,185,129,0.85)',
  };

  return (
    <button
      onClick={onSelect}
      className="group relative rounded-xl overflow-hidden transition-all duration-150 text-left"
      style={{ aspectRatio: '1', background: 'var(--t-panel)', border: '2px solid transparent' }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--brand-primary)'; e.currentTarget.style.transform = 'scale(1.02)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.transform = 'scale(1)'; }}
      title={asset.originalFilename}
    >
      {asset.type === 'image' ? (
        <img
          src={resolveMediaUrl(asset.thumbnailUrl || asset.url)}
          alt={asset.originalFilename}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      ) : asset.type === 'video' ? (
        <div className="w-full h-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#1a1a2e,#16213e)' }}>
          {asset.posterUrl
            ? <img src={resolveMediaUrl(asset.posterUrl)} alt="" className="w-full h-full object-cover" />
            : <Film size={22} className="text-white/40" />
          }
        </div>
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center gap-2" style={{ background: 'linear-gradient(135deg,#2d1b69,#11998e)' }}>
          <Music size={22} className="text-white/50" />
          <span className="text-[9px] font-medium text-white/60 px-2 text-center truncate w-full">
            {asset.originalFilename}
          </span>
        </div>
      )}

      {/* Type badge */}
      <div
        className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded text-[9px] font-bold capitalize"
        style={{ background: TYPE_COLORS[asset.type] || 'rgba(100,116,139,0.85)', color: '#fff', backdropFilter: 'blur(4px)' }}
      >
        {asset.type}
      </div>

      {/* Hover overlay */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2"
        style={{ background: 'linear-gradient(to top, rgba(67,97,238,0.85) 0%, transparent 60%)' }}
      >
        <p className="text-white text-[10px] font-semibold truncate w-full leading-tight">
          {asset.originalFilename}
        </p>
      </div>

      {/* Select checkmark on hover */}
      <div
        className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hidden group-hover:flex"
        style={{ background: 'var(--brand-primary)' }}
      >
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
          <path d="M2 5l2.5 2.5L8 3" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </button>
  );
}
